import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  BarChart3,
  DollarSign,
  AlertCircle,
  Shield,
  Clock,
  Briefcase,
  Calendar,
} from "lucide-react";
import { reportsApi } from "../../services/api";
import RevenueReport from "./RevenueReport";
import OutstandingReport from "./OutstandingReport";
import TrustBalancesReport from "./TrustBalancesReport";
import TimeSummaryReport from "./TimeSummaryReport";
import MatterOverviewReport from "./MatterOverviewReport";

type TabKey = "revenue" | "outstanding" | "trust" | "time" | "matters";

const tabs: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: "revenue", label: "Revenue", icon: <DollarSign className="h-4 w-4" /> },
  { key: "outstanding", label: "Outstanding", icon: <AlertCircle className="h-4 w-4" /> },
  { key: "trust", label: "Trust", icon: <Shield className="h-4 w-4" /> },
  { key: "time", label: "Time", icon: <Clock className="h-4 w-4" /> },
  { key: "matters", label: "Matters", icon: <Briefcase className="h-4 w-4" /> },
];

const ReportsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>("revenue");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Data states
  const [revenueData, setRevenueData] = useState<any>(null);
  const [outstandingData, setOutstandingData] = useState<any>(null);
  const [trustData, setTrustData] = useState<any>(null);
  const [timeData, setTimeData] = useState<any>(null);
  const [matterData, setMatterData] = useState<any>(null);

  // Loading states
  const [loading, setLoading] = useState<Record<TabKey, boolean>>({
    revenue: false,
    outstanding: false,
    trust: false,
    time: false,
    matters: false,
  });

  const buildParams = useCallback(() => {
    const params: Record<string, any> = {};
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    return params;
  }, [dateFrom, dateTo]);

  const fetchData = useCallback(
    async (tab: TabKey) => {
      setLoading((prev) => ({ ...prev, [tab]: true }));
      const params = buildParams();

      try {
        switch (tab) {
          case "revenue": {
            const res = await reportsApi.revenue(params);
            setRevenueData(res.data);
            break;
          }
          case "outstanding": {
            const res = await reportsApi.outstandingInvoices(params);
            setOutstandingData(res.data);
            break;
          }
          case "trust": {
            const res = await reportsApi.trustBalances(params);
            setTrustData(res.data);
            break;
          }
          case "time": {
            const res = await reportsApi.timeSummary(params);
            setTimeData(res.data);
            break;
          }
          case "matters": {
            const res = await reportsApi.matterOverview(params);
            setMatterData(res.data);
            break;
          }
        }
      } catch (err) {
        console.error(`Failed to fetch ${tab} report:`, err);
      } finally {
        setLoading((prev) => ({ ...prev, [tab]: false }));
      }
    },
    [buildParams]
  );

  // Fetch data when tab changes or date filters change
  useEffect(() => {
    fetchData(activeTab);
  }, [activeTab, fetchData]);

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-brand-light">
                <BarChart3 className="h-6 w-6 text-brand-accent" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-800">Reports</h1>
                <p className="text-sm text-slate-500 mt-0.5">
                  Financial analytics and firm performance
                </p>
              </div>
            </div>
          </div>

          {/* Date Range Filters */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-slate-400" />
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="From"
              />
              <span className="text-slate-400 text-sm">to</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="To"
              />
            </div>
            {(dateFrom || dateTo) && (
              <button
                onClick={() => {
                  setDateFrom("");
                  setDateTo("");
                }}
                className="text-xs text-slate-500 hover:text-slate-700 underline"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tab Navigation - Pill Style */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-1.5 mb-6 inline-flex gap-1">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className="relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
            style={{
              color: activeTab === tab.key ? "#ffffff" : "#64748b",
            }}
          >
            {activeTab === tab.key && (
              <motion.div
                layoutId="reports-tab-pill"
                className="absolute inset-0 rounded-lg"
                style={{ backgroundColor: "var(--color-primary)" }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            )}
            <span className="relative z-10 flex items-center gap-2">
              {tab.icon}
              {tab.label}
            </span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {activeTab === "revenue" && (
          <RevenueReport
            dateFrom={dateFrom}
            dateTo={dateTo}
            data={revenueData}
            loading={loading.revenue}
          />
        )}
        {activeTab === "outstanding" && (
          <OutstandingReport data={outstandingData} loading={loading.outstanding} />
        )}
        {activeTab === "trust" && (
          <TrustBalancesReport data={trustData} loading={loading.trust} />
        )}
        {activeTab === "time" && (
          <TimeSummaryReport data={timeData} loading={loading.time} />
        )}
        {activeTab === "matters" && (
          <MatterOverviewReport data={matterData} loading={loading.matters} />
        )}
      </motion.div>
    </div>
  );
};

export default ReportsPage;
